import React, { useEffect, useState } from "react";
import { useNotify, useDataProvider, TextInput, SimpleForm, usePermissions } from "react-admin";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CardActions,
  Typography,
  Box,
  Paper,
  Grid,
  Dialog,
  DialogActions,
  Button,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TextField,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
} from "@mui/material";
import { ChangelogReact } from "../components/Changelog";
import { permissionCheck, renderIfPermissions } from "../api/permissionManager";

export const CustomReleaseCreate = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();

  const handleSubmit = async (data) => {
    await dataProvider
      .create("app_releases", { data })
      .then(({ response }) => {
        notify("Release Created Successfully", { type: "success" });
        navigate(`/app_releases/`);
      })
      .catch((error) => {
        if (error.message) {
          notify(`Error creating release: ${error.message}`, { type: "error", autoHideDuration: 10000 });
        } else {
          notify(`Error creating release: ${error.status}. Check Sentry.`, { type: "error", autoHideDuration: 10000 });
        }
      });
  };

  return (
    <Paper elevation={3} style={{ padding: "16px", margin: "16px" }}>
      <Typography variant="h5" gutterBottom>
        Create New Release
      </Typography>
      <SimpleForm onSubmit={handleSubmit}>
        <TextInput label="Release Name" source="release_name" fullWidth />
        <TextInput label="iOS Tag" source="ios_tag" fullWidth />
        <TextInput label="Android Tag" source="android_tag" fullWidth />
      </SimpleForm>
    </Paper>
  );
};

export const CustomReleaseShow = () => {
  const { permissions } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const [changelog, setChangelog] = useState([]);
  const record = location.state?.record;
  const notify = useNotify();

  const getChangelog = async () => {
    const data = await dataProvider.getManyReference("changelog", {
      filter: { from: "app_releases" },
      id: record.id,
      target: "id",
    });
    setChangelog(data);
  };

  useEffect(() => {
    if (permissionCheck("app_releases", "write", permissions)) getChangelog();
  }, []);

  const RenderChangelog = () => {
    if (changelog.data) {
      return <ChangelogReact entries={changelog.data} />;
    }
  };

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const handleOpenDeleteDialog = () => setOpenDeleteDialog(true);
  const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);

  const handleEdit = () => {
    navigate(`../app_releases/${record.id}/edit`, { state: { record }, replace: true });
  };
  const handleDelete = async () => {
    await dataProvider
      .delete("app_releases", { id: record.id })
      .then(({ response }) => {
        notify(`Release "${record.id}" deleted successfully`, { type: "success" });
        navigate("/app_releases");
      })
      .catch((error) => {
        notify("Error deleting release", { type: "error" });
      });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={7}>
        <Paper elevation={3} sx={{ margin: "16px", padding: "16px" }}>
          <Typography data-testid={record.id}>
            <strong>ID:</strong> {record.id}
          </Typography>
          <Typography>
            <strong>iOS Tag:</strong> {record.ios_tag || "Null"}
          </Typography>
          <Typography>
            <strong>Android Tag:</strong> {record.android_tag || "Null"}
          </Typography>
          <Typography>
            <strong>Created At:</strong> {record.created_at}
          </Typography>

          {renderIfPermissions(
            permissions,
            "app_releases",
            "write",
            <CardActions
              sx={{ display: "flex", justifyContent: "flex-start", gap: 2, padding: "8px 0 0 0", marginTop: "3px" }}
            >
              <Button variant="contained" color="primary" onClick={handleEdit} sx={{ minWidth: 50 }}>
                Edit
              </Button>
              <Button variant="contained" color="error" onClick={handleOpenDeleteDialog} sx={{ minWidth: 50 }}>
                Delete
              </Button>
            </CardActions>
          )}
          <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
            <DialogTitle>Delete Tag?</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete the "<strong>{record.id}</strong>" release? This action cannot be
                undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDeleteDialog} color="primary">
                Cancel
              </Button>
              <Button onClick={handleDelete} color="error" variant="contained">
                Yes, Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Grid>
      {renderIfPermissions(
        permissions,
        "clients",
        "write",
        <Grid item xs={5}>
          <RenderChangelog />
        </Grid>
      )}
    </Grid>
  );
};

export const CustomReleaseEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const record = location.state?.record;

  if (!record) {
    return (
      <Paper elevation={3} style={{ padding: "16px" }}>
        <Typography>No record found</Typography>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </Paper>
    );
  }

  const handleSubmit = async (data) => {
    await dataProvider
      .update("app_releases", {
        id: record.id,
        data,
        previousData: record,
      })
      .then(({ response }) => {
        notify("Release updated successfully", { type: "success" });
        navigate(`/app_releases/${record.id}/show`, { state: { record: { ...record, ...data } } });
      })
      .catch((error) => {
        notify("Error updating release", { type: "error" });
      });
  };

  return (
    <Paper elevation={3} style={{ padding: "16px", margin: "16px" }}>
      <Typography variant="h5" gutterBottom>
        Edit Release
      </Typography>

      <SimpleForm onSubmit={handleSubmit} defaultValues={record}>
        <TextInput disabled label="Release Name" source="release_name" fullWidth />
        <TextInput label="iOS Tag" source="ios_tag" fullWidth />
        <TextInput label="Android Tag" source="android_tag" fullWidth />
      </SimpleForm>
    </Paper>
  );
};

export const CustomReleaseList = () => {
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const [releases, setReleases] = useState([]);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filtered, setFiltered] = useState([]);

  useEffect(() => {
    dataProvider.getList("app_releases").then(({ data }) => setReleases(data));
  }, []);

  useEffect(() => {
    let data = [...releases];

    if (search) {
      const lowerCaseSearch = search.toLowerCase();
      data = data.filter(
        (release) => release.release_name && release.release_name.toLowerCase().includes(lowerCaseSearch)
      );
    }

    data.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      if (!aValue) return 1;
      if (!bValue) return -1;
      if (sortField === "created_at") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    setFiltered(data);
  }, [search, releases, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleRowClick = (id, record) => {
    navigate(`/app_releases/${id}/show`, { state: { record } });
  };

  const handleCreate = () => {
    navigate(`../create`);
  };

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent={"space-around"}
        gap={2}
        sx={{ paddingTop: "1em", paddingBottom: "1em" }}
      >
        <Typography variant="h5">App Releases</Typography>
        <Box flexGrow={1}>
          <TextField fullWidth label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Box>
        {renderIfPermissions(
          permissions,
          "app_releases",
          "write",
          <Button variant="contained" color="primary" onClick={handleCreate}>
            Create New Release
          </Button>
        )}
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortField === "release_name"}
                direction={sortField === "release_name" ? sortOrder : "asc"}
                onClick={() => handleSort("release_name")}
              >
                Release Name
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === "ios_tag"}
                direction={sortField === "ios_tag" ? sortOrder : "asc"}
                onClick={() => handleSort("ios_tag")}
              >
                iOS Tag
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === "android_tag"}
                direction={sortField === "android_tag" ? sortOrder : "asc"}
                onClick={() => handleSort("android_tag")}
              >
                Android Tag
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortField === "created_at"}
                direction={sortField === "created_at" ? sortOrder : "asc"}
                onClick={() => handleSort("created_at")}
              >
                Created At
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((r) => (
            <TableRow key={r.release_name} hover onClick={() => handleRowClick(r.release_name, r)}>
              <TableCell>{r.release_name}</TableCell>
              <TableCell>{r.ios_tag}</TableCell>
              <TableCell>{r.android_tag}</TableCell>
              <TableCell>{r.created_at}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
